import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Divider,
  Box,
  Chip,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Tooltip,
} from '@mui/material';
import {
  Login,
  AccountCircle,
  Dashboard,
  Logout,
  Paragliding,
  Person,
  Apps,
  Storefront,
  NotificationsActive,
  DirectionsBus,
  Menu as MenuIcon,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Groups2,
  People,
  BadgeOutlined,
  Timeline,
  Announcement,
  Assessment,
  Key,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { logoutUser } from '../../store/slices/authSlice';
import hfssLogo from '../../assets/hfss_logo.png';
import { useTranslation } from 'react-i18next';
import HeaderTimeWeather from './HeaderTimeWeather';
import WeatherCarousel from './WeatherCarousel';
import LoginModal from '../modals/LoginModal';
import ServiceAuthModal from '../ServiceAuthModal';
import { useUnifiedAuth } from '../../contexts/UnifiedAuthContext';
import PilotServiceLink from '../PilotServiceLink';
import RoleSwitcher from '../RoleSwitcher';

interface AppHeaderProps {
  title?: string;
  showAuthButtons?: boolean;
}

const drawerWidthExpanded = 240;
const drawerWidthCollapsed = 65;

const AppHeader: React.FC<AppHeaderProps> = ({ 
  showAuthButtons = true
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(null);
  const [servicesAnchorEl, setServicesAnchorEl] = useState<null | HTMLElement>(null);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [serviceAuthModalOpen, setServiceAuthModalOpen] = useState(false);
  const [serviceIntent, setServiceIntent] = useState<'shuttle' | 'equipment' | 'marketplace' | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerCollapsed, setDrawerCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved !== null ? saved === 'true' : true; // Default to collapsed
  });
  
  // Use unified auth context for complete auth status
  const unifiedAuth = useUnifiedAuth();
  
  // Keep Redux selectors for platform-specific features
  const platformUser = useSelector((state: RootState) => state.auth.user);
  const isPlatformAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  
  // Use unified auth for display
  const isAuthenticated = unifiedAuth.isAuthenticated;
  const user = unifiedAuth.platformUser || { 
    email: unifiedAuth.email, 
    name: unifiedAuth.displayName,
    is_pilot: unifiedAuth.userType === 'pilot',
    is_platform_admin: unifiedAuth.userType === 'platform_admin',
    user_type: unifiedAuth.userType === 'club_manager' ? 'club_manager' : undefined
  };

  // Check if we should show sidebar based on route
  const shouldShowSidebar = location.pathname.startsWith('/app/');

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageAnchorEl(null);
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    handleLanguageMenuClose();
  };

  const handleServicesMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setServicesAnchorEl(event.currentTarget);
  };

  const handleServicesMenuClose = () => {
    setServicesAnchorEl(null);
  };

  const handleServiceClick = (service: 'marketplace' | 'shuttle' | 'equipment') => {
    handleServicesMenuClose();
    
    // Check if user is authenticated (either platform or service)
    if (isAuthenticated) {
      // Navigate directly to service if authenticated
      if (service === 'marketplace') {
        navigate('/app/marketplace');
      } else if (service === 'shuttle') {
        navigate('/app/shuttle-booking');
      } else if (service === 'equipment') {
        navigate('/app/reminders/dashboard');
      }
    } else {
      // Show authentication modal if not authenticated
      setServiceIntent(service);
      setServiceAuthModalOpen(true);
    }
  };

  const handleLogout = async () => {
    try {
      // Clear all auth data immediately
      localStorage.clear(); // Clear ALL localStorage to ensure no tokens remain
      sessionStorage.clear(); // Clear sessionStorage too
      
      // Dispatch logout to clear Redux state
      await dispatch(logoutUser());
      
      // Clear service auth
      unifiedAuth.refreshServiceAuth();
      
      // Close menu
      handleClose();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // ALWAYS redirect, even if there's an error
      // Use replace to prevent back button from returning to protected page
      window.location.replace('/');
    }
  };

  const handleCloseServiceAuthModal = () => {
    setServiceAuthModalOpen(false);
    setServiceIntent(null);
  };

  const navigateToDashboard = () => {
    handleClose();
    if (platformUser?.is_platform_admin || platformUser?.user_type === 'club_manager') {
      navigate('/app/dashboard');
    } else if (platformUser?.user_type === 'pilot' || platformUser?.is_pilot) {
      navigate('/app/pilot-dashboard');
    } else {
      navigate('/app/dashboard');
    }
  };

  const handleToggleDrawer = () => {
    const newState = !drawerCollapsed;
    setDrawerCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', newState.toString());
    // Dispatch custom event for same-window updates
    window.dispatchEvent(new Event('sidebarToggle'));
  };

  // Define menu items based on user type
  interface MenuItem {
    text: string;
    icon: React.ReactElement;
    path: string;
    group?: string;
    isServiceLink?: boolean;
  }
  
  let menuItems: MenuItem[] = [];

  if (isPlatformAuthenticated && platformUser) {
    // For pilot-only users
    if (platformUser.user_type === 'pilot' && !platformUser.is_platform_admin) {
      menuItems = [
        // Pilot group
        { text: t('menu.pilotDashboard'), icon: <Paragliding />, path: '/app/pilot-dashboard', group: 'Pilot' },

        // Services group
        { text: t('menu.shuttleBooking'), icon: <DirectionsBus />, path: '/app/shuttle-booking', group: 'Services', isServiceLink: true },
        { text: t('menu.equipmentReminders'), icon: <Assessment />, path: '/app/reminders/dashboard', group: 'Services', isServiceLink: true },
        { text: t('menu.marketplace'), icon: <Storefront />, path: '/app/marketplace', group: 'Services', isServiceLink: true },
      ];
    } else {
      // For club managers and platform admins
      menuItems = [
        // Main items (no group)
        { text: t('menu.dashboard'), icon: <Dashboard />, path: '/app/dashboard' },
        { text: t('menu.cards'), icon: <CreditCard />, path: '/app/cards' },
        { text: t('menu.visitorCards'), icon: <BadgeOutlined />, path: '/app/visitors' },
      ];

      // Admin group
      menuItems.push(
        { text: t('menu.clubs'), icon: <Groups2 />, path: '/app/admin/clubs', group: 'Admin' }
      );

      if (platformUser.user_type === 'club_manager' || platformUser.is_platform_admin) {
        menuItems.push(
          { text: t('menu.users'), icon: <People />, path: '/app/admin/users', group: 'Admin' }
        );
      }

      if (platformUser.is_platform_admin || (platformUser.clubs && platformUser.clubs.length > 0)) {
        menuItems.push(
          { text: t('menu.shuttles'), icon: <DirectionsBus />, path: '/app/shuttles', group: 'Admin' },
          { text: t('menu.shuttleReports'), icon: <Assessment />, path: '/app/shuttle-reports', group: 'Admin' }
        );
      }

      if (platformUser.is_platform_admin) {
        menuItems.push(
          { text: t('menu.reminders'), icon: <NotificationsActive />, path: '/app/admin/reminders', group: 'Admin' },
          { text: 'Activities', icon: <Timeline />, path: '/app/admin/activities', group: 'Admin' },
          { text: 'Platform News', icon: <Announcement />, path: '/app/admin/platform-news', group: 'Admin' }
        );
      }

      // Pilot group for dual role users
      if (platformUser.is_pilot) {
        menuItems.push(
          { text: t('menu.pilotDashboard'), icon: <Paragliding />, path: '/app/pilot-dashboard', group: 'Pilot' }
        );
      }

      // Services group
      menuItems.push(
        { text: t('menu.shuttleBooking'), icon: <DirectionsBus />, path: '/app/shuttle-booking', group: 'Services', isServiceLink: platformUser.is_pilot },
        { text: t('menu.equipmentReminders'), icon: <Assessment />, path: '/app/reminders/dashboard', group: 'Services', isServiceLink: platformUser.is_pilot },
        { text: t('menu.marketplace'), icon: <Storefront />, path: '/app/marketplace', group: 'Services', isServiceLink: platformUser.is_pilot }
      );
    }
  }

  const currentDrawerWidth = drawerCollapsed ? drawerWidthCollapsed : drawerWidthExpanded;

  // Group menu items by their group property
  const groupedMenuItems = menuItems.reduce((acc, item) => {
    const group = item.group || 'main';
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {} as Record<string, MenuItem[]>);

  const drawer = shouldShowSidebar && isPlatformAuthenticated ? (
    <Box sx={{ 
      height: '100%',
      bgcolor: '#f8f9fa', 
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Vertical collapse button on right edge */}
      <Box sx={{
        position: 'absolute',
        right: -12,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1,
        bgcolor: '#f8f9fa',
        borderRadius: '0 4px 4px 0',
        boxShadow: 1,
        display: 'flex',
        alignItems: 'center',
        height: 60,
        width: 12,
        '&:hover': {
          width: 16,
          cursor: 'pointer',
        },
        transition: 'width 0.2s ease',
      }}
      onClick={handleToggleDrawer}
      >
        <IconButton 
          size="small" 
          sx={{ 
            p: 0,
            minWidth: 'auto',
            color: 'text.secondary',
          }}
        >
          {drawerCollapsed ? <ChevronRight sx={{ fontSize: 16 }} /> : <ChevronLeft sx={{ fontSize: 16 }} />}
        </IconButton>
      </Box>

      {/* Scrollable menu area */}
      <Box sx={{ 
        flex: 1,
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderRadius: '3px',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.3)',
          },
        },
      }}>
        <List sx={{ p: drawerCollapsed ? 1 : 2, pt: drawerCollapsed ? 1 : 3 }}>
          {Object.entries(groupedMenuItems).map(([group, items]) => (
            <Box key={group}>
              {group !== 'main' && !drawerCollapsed && (
                <ListSubheader 
                  disableSticky
                  sx={{ 
                    bgcolor: 'transparent',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'text.secondary',
                    pl: 2,
                    pt: group === Object.keys(groupedMenuItems)[0] ? 0 : 2,
                    lineHeight: 'normal',
                  }}
                >
                  {group}
                </ListSubheader>
              )}
              {group !== 'main' && drawerCollapsed && items.length > 0 && (
                <Divider sx={{ my: 1 }} />
              )}
              {items.map((item) => 
                item.isServiceLink && platformUser?.user_type === 'pilot' ? (
                  <PilotServiceLink
                    key={item.text}
                    text={item.text}
                    icon={item.icon}
                    servicePath={item.path}
                    collapsed={drawerCollapsed}
                  />
                ) : drawerCollapsed ? (
                  <Tooltip key={item.text} title={item.text} placement="right">
                    <ListItemButton
                      component={Link}
                      to={item.path}
                      selected={location.pathname === item.path}
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        justifyContent: 'center',
                        '&:hover': {
                          bgcolor: 'primary.light',
                          color: 'white',
                          '& .MuiListItemIcon-root': {
                            color: 'white',
                          },
                        },
                        '&.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'white',
                          '& .MuiListItemIcon-root': {
                            color: 'white',
                          },
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, color: location.pathname === item.path ? 'white' : 'inherit' }}>
                        {item.icon}
                      </ListItemIcon>
                    </ListItemButton>
                  </Tooltip>
                ) : (
                  <ListItemButton
                    key={item.text}
                    component={Link}
                    to={item.path}
                    selected={location.pathname === item.path}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      '&:hover': {
                        bgcolor: 'primary.light',
                        color: 'white',
                        '& .MuiListItemIcon-root': {
                          color: 'white',
                        },
                      },
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'white',
                        '& .MuiListItemIcon-root': {
                          color: 'white',
                        },
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: location.pathname === item.path ? 'white' : 'inherit' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                )
              )}
            </Box>
          ))}
        </List>
      </Box>
      
      {/* Footer at bottom of sidebar */}
      <Box sx={{ 
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: '#f8f9fa',
        p: drawerCollapsed ? 0.5 : 1,
        mt: 'auto',
      }}>
        {!drawerCollapsed && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: 'text.secondary',
            fontSize: '0.75rem',
            textAlign: 'center',
            py: 1,
          }}>
            HFSS Club Management
          </Box>
        )}
      </Box>
    </Box>
  ) : null;

  return (
    <>
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: '#fff', 
          color: '#333', 
          boxShadow: 2,
        }}
      >
        <Toolbar>
          {shouldShowSidebar && isPlatformAuthenticated && (
            <IconButton
              color="inherit"
              aria-label="toggle drawer"
              onClick={() => setDrawerOpen(!drawerOpen)}
              edge="start"
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <IconButton
            edge="start"
            color="inherit"
            aria-label="home"
            onClick={() => navigate('/')}
            sx={{ mr: 2 }}
          >
            <img src={hfssLogo} alt="HFSS" style={{ height: 40 }} />
          </IconButton>
          
          <Box
            component={Link}
            to="/"
            sx={{
              flexGrow: 1,
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: 1.5,
              textDecoration: 'none',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'translateX(2px)',
              },
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                fontSize: { sm: '1.4rem', md: '1.6rem' },
                letterSpacing: '-0.03em',
                fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                position: 'relative',
              }}
            >
              HFSS
            </Typography>
            <Box
              sx={{
                height: 24,
                width: 2,
                background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
                opacity: 0.3,
                borderRadius: 1,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                fontSize: { sm: '1.1rem', md: '1.2rem' },
                letterSpacing: '-0.01em',
                fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                color: '#4a5568',
              }}
            >
              Clubs
            </Typography>
            <Box
              sx={{
                backgroundColor: '#667eea',
                color: 'white',
                px: 1,
                py: 0.3,
                borderRadius: 1,
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                display: { xs: 'none', lg: 'inline-block' },
                mr: 2,
              }}
            >
              Beta
            </Box>
          </Box>
          
          {/* Mobile Title - Shorter */}
          <Box
            component={Link}
            to="/"
            sx={{
              flexGrow: 1,
              display: { xs: 'flex', sm: 'none' },
              alignItems: 'center',
              gap: 1,
              textDecoration: 'none',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                fontSize: '1.2rem',
                letterSpacing: '-0.02em',
                fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              HFSS
            </Typography>
          </Box>

          {/* Spacer to push time/weather to center - hidden on mobile */}
          <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />

          {/* Time and Weather Container - Hidden on mobile */}
          <Box sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 2,
            flexShrink: 0,
          }}>
            {/* Time Display */}
            <HeaderTimeWeather />

            {/* Separator */}
            <Typography
              variant="body2"
              sx={{
                color: '#e0e0e0',
                fontWeight: 300,
                display: { xs: 'none', lg: 'block' },
              }}
            >
              |
            </Typography>

            {/* Weather Carousel */}
            <WeatherCarousel />
          </Box>

          {/* Spacer to balance the center - hidden on mobile */}
          <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />

          {/* Mobile spacer */}
          <Box sx={{ flexGrow: 1, display: { xs: 'block', md: 'none' } }} />

          {/* Services Menu Button - Always visible */}
          <IconButton
            size="large"
            aria-label="services menu"
            aria-controls="services-menu"
            aria-haspopup="true"
            onClick={handleServicesMenuOpen}
            color="inherit"
            sx={{ mr: 1 }}
          >
            <Apps />
          </IconButton>
          
          <Menu
            id="services-menu"
            anchorEl={servicesAnchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(servicesAnchorEl)}
            onClose={handleServicesMenuClose}
          >
            <MenuItem onClick={() => handleServiceClick('marketplace')}>
              <Storefront sx={{ mr: 2 }} />
              {t('navigation.marketplace')}
            </MenuItem>
            <MenuItem onClick={() => handleServiceClick('shuttle')}>
              <DirectionsBus sx={{ mr: 2 }} />
              {t('navigation.shuttleBooking')}
            </MenuItem>
            <MenuItem onClick={() => handleServiceClick('equipment')}>
              <NotificationsActive sx={{ mr: 2 }} />
              {t('navigation.equipmentReminders')}
            </MenuItem>
          </Menu>

          {showAuthButtons && (
            <>
              {isAuthenticated && user ? (
                <>
                  {/* Dashboard button for platform users - hidden on mobile, accessible via menu */}
                  {unifiedAuth.userType !== 'guest' && (
                    <Button
                      color="primary"
                      variant="contained"
                      startIcon={<Dashboard />}
                      onClick={navigateToDashboard}
                      sx={{
                        mr: { xs: 1, sm: 2 },
                        display: { xs: 'none', sm: 'flex' },
                      }}
                    >
                      {t('menu.dashboard')}
                    </Button>
                  )}

                  <IconButton
                    size="large"
                    aria-label="account of current user"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleMenu}
                    color="inherit"
                  >
                    <AccountCircle />
                  </IconButton>
                  <Menu
                    id="menu-appbar"
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                  >
                    <Box sx={{ px: 2, py: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {unifiedAuth.email || unifiedAuth.displayName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip
                          size="small"
                          icon={unifiedAuth.userType === 'guest' ? <Person /> : undefined}
                          label={
                            unifiedAuth.userType === 'platform_admin' ? t('userType.platformAdmin') :
                            unifiedAuth.userType === 'club_manager' ? t('userType.clubManager') :
                            unifiedAuth.userType === 'pilot' ? t('userType.pilot') :
                            unifiedAuth.userType === 'guest' ? t('userType.serviceGuest') :
                            t('userType.user')
                          }
                          color={unifiedAuth.userType === 'guest' ? 'default' : 'primary'}
                          variant={unifiedAuth.userType === 'guest' ? 'outlined' : 'filled'}
                        />
                      </Box>
                    </Box>
                    <Divider />
                    {/* Role Switcher for dual-role users */}
                    {platformUser?.available_roles && platformUser.available_roles.length > 1 && [
                      <RoleSwitcher key="role-switcher" onClose={handleClose} />,
                      <Divider key="role-divider" />
                    ]}
                    {unifiedAuth.userType !== 'guest' && [
                      <MenuItem key="dashboard" onClick={navigateToDashboard}>
                        <Dashboard sx={{ mr: 1 }} /> {t('menu.dashboard')}
                      </MenuItem>,
                      <MenuItem key="profile" onClick={() => { handleClose(); navigate('/app/profile'); }}>
                        <Person sx={{ mr: 1 }} /> {t('navigation.profile')}
                      </MenuItem>,
                      (unifiedAuth.userType === 'platform_admin' || unifiedAuth.userType === 'club_manager') ? (
                        <MenuItem key="api-token" onClick={() => { handleClose(); navigate('/app/api-token'); }}>
                          <Key sx={{ mr: 1 }} /> {t('navigation.apiToken')}
                        </MenuItem>
                      ) : null,
                      (!platformUser?.is_pilot) ? (
                        <MenuItem key="join-club" onClick={() => { handleClose(); navigate('/registrations'); }}>
                          <Paragliding sx={{ mr: 1 }} /> {t('landing.cta.pilotRegistration')}
                        </MenuItem>
                      ) : null,
                      <Divider key="divider" />
                    ].filter(Boolean)}
                    <MenuItem onClick={handleLogout}>
                      <Logout sx={{ mr: 1 }} /> {t('menu.logout')}
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Paragliding />}
                    onClick={() => navigate('/registrations')}
                    sx={{
                      mr: 1,
                      minWidth: { xs: 'auto', sm: 64 },
                      px: { xs: 1, sm: 2 },
                      py: { xs: 0.5, sm: 1 },
                      height: { xs: 36, sm: 'auto' },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      '& .MuiButton-startIcon': {
                        marginRight: { xs: 0, sm: 1 },
                        display: { xs: 'none', sm: 'flex' },
                      },
                    }}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                      {t('landing.cta.pilotRegistration')}
                    </Box>
                    <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                      {t('landing.cta.joinClub')}
                    </Box>
                  </Button>
                  <Button
                    color="inherit"
                    startIcon={<Login />}
                    onClick={() => setLoginModalOpen(true)}
                    sx={{
                      minWidth: { xs: 'auto', sm: 64 },
                      px: { xs: 1, sm: 2 },
                      '& .MuiButton-startIcon': {
                        marginRight: { xs: 0, sm: 1 },
                      },
                    }}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      {t('landing.cta.login')}
                    </Box>
                  </Button>
                </>
              )}
            </>
          )}

          {/* Language Switcher - Last element on the right */}
          <IconButton onClick={handleLanguageMenuOpen} color="inherit" sx={{ fontSize: '1.5rem' }}>
            {i18n.language?.startsWith('it') ? '🇮🇹' : '🇬🇧'}
          </IconButton>
          <Menu
            anchorEl={languageAnchorEl}
            open={Boolean(languageAnchorEl)}
            onClose={handleLanguageMenuClose}
          >
            <MenuItem onClick={() => changeLanguage('en')}>
              <Box component="span" sx={{ mr: 1 }}>🇬🇧</Box> English
            </MenuItem>
            <MenuItem onClick={() => changeLanguage('it')}>
              <Box component="span" sx={{ mr: 1 }}>🇮🇹</Box> Italiano
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {shouldShowSidebar && isPlatformAuthenticated && (
        <>
          {/* Mobile Drawer */}
          <Drawer
            variant="temporary"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: currentDrawerWidth,
                top: 56, // Below mobile header
                height: 'calc(100vh - 56px)',
              },
              '& .MuiBackdrop-root': {
                top: 56, // Backdrop also below header
              },
            }}
          >
            {drawer}
          </Drawer>
          
          {/* Desktop Drawer */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: currentDrawerWidth,
                transition: 'width 0.3s ease-in-out',
                overflow: 'visible', // Allow collapse button to overflow
                position: 'fixed',
                top: 64, // Below header
                bottom: 0,
                left: 0,
                border: 'none',
                height: 'calc(100vh - 64px)',
                bgcolor: '#f8f9fa',
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </>
      )}

      <LoginModal 
        open={loginModalOpen} 
        onClose={() => setLoginModalOpen(false)} 
      />
      
      <ServiceAuthModal
        open={serviceAuthModalOpen}
        onClose={handleCloseServiceAuthModal}
        intent={serviceIntent}
      />
    </>
  );
};

export default AppHeader;